/**
 * logger.ts
 *
 * Structured browser logger — the only sanctioned path for feature code
 * to emit diagnostic output.  All payloads are passed through `redact`
 * before being emitted, so PII never reaches the console or any sink.
 *
 * Rules:
 * - debug/info are silenced in production (no-op).
 * - warn/error always emit (they indicate real problems).
 * - Feature code MUST NOT call console.log / console.debug directly.
 */

import { redact } from './redact.js';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogEntry = {
  level: LogLevel;
  message: string;
  /** Arbitrary structured context — PII will be scrubbed. */
  context?: unknown;
  timestamp: string;
};

export type LogSink = (entry: LogEntry) => void;

type LoggerConfig = {
  /** Current environment — debug/info are silenced when this is 'production'. */
  environment?: string;
  /** Optional additional sink (e.g. Sentry breadcrumb writer). */
  sink?: LogSink;
};

let _config: LoggerConfig = {
  // import.meta.env is available in Vite/Vitest; fall back gracefully in test environments.
  environment: (import.meta.env?.['MODE'] as string | undefined) ?? 'development',
};

/**
 * Call once during `initObservability` to wire up the environment and
 * optional secondary sink (e.g. Sentry breadcrumb forwarder).
 */
export function configureLogger(config: LoggerConfig): void {
  _config = { ..._config, ...config };
}

function isProduction(): boolean {
  return _config.environment === 'production';
}

function emit(level: LogLevel, message: string, context?: unknown): void {
  const safeContext = context !== undefined ? redact(context) : undefined;
  const entry: LogEntry = {
    level,
    message,
    context: safeContext,
    timestamp: new Date().toISOString(),
  };

  _config.sink?.(entry);

  /* eslint-disable no-restricted-globals */
  switch (level) {
    case 'debug':
      // biome-ignore lint/suspicious/noConsole: this module IS the sanctioned console sink (see header).
      if (!isProduction()) console.debug('[hostesa:debug]', message, safeContext ?? '');
      break;
    case 'info':
      // biome-ignore lint/suspicious/noConsole: this module IS the sanctioned console sink (see header).
      if (!isProduction()) console.info('[hostesa:info]', message, safeContext ?? '');
      break;
    case 'warn':
      console.warn('[hostesa:warn]', message, safeContext ?? '');
      break;
    case 'error':
      console.error('[hostesa:error]', message, safeContext ?? '');
      break;
  }
}

export const logger = {
  debug(message: string, context?: unknown): void {
    emit('debug', message, context);
  },
  info(message: string, context?: unknown): void {
    emit('info', message, context);
  },
  warn(message: string, context?: unknown): void {
    emit('warn', message, context);
  },
  error(message: string, context?: unknown): void {
    emit('error', message, context);
  },
};
